import { FC } from 'react'
import { unstable_useBlocker as useBlocker, useNavigate } from 'react-router-dom'

import { BarChart, Box, Colors, colors as gammaColors } from '@factorialco/deprecated-design-system'
import { Widget } from '@factorialco/f0-react/dist/experimental'
import { PerformanceInsightDocument, PerformanceInsightQuery } from 'generated/resources/graphql'
import { findLast, max } from 'lodash'
import { Moment } from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { formatBackendDateToMonthYear, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { buildWidget } from 'modules/core/lib/widgets'

import { colorForEmployeeScore } from 'modules/performance_process/lib/employeeScoreColor'

import './index.styles.css'
interface ChartData {
  bucket: string
  color?: Colors
  score?: number
  scaleMin?: number
  scaleMax?: number
  date?: Moment
  reviewId?: number
  targetId?: string
}

const prepareData = ({ performance }: PerformanceInsightQuery) => {
  const nodes = [...performance.reviewProcessTargetsConnection.nodes].reverse()

  const chartData: ChartData[] = nodes
    .map((node, index) => {
      const startsAt = node.performanceReviewProcess?.startsAt
      const { score, scaleMin, scaleMax } = node.reviewFinalEmployeeScore ?? {}

      const startsAtDate = startsAt ? stringToDate(startsAt, 'backend_datetime') : undefined
      const label = startsAtDate ? formatBackendDateToMonthYear(startsAtDate) : undefined

      if (!label) return

      const color = score && scaleMax ? colorForEmployeeScore(score, scaleMax) : 'grey000White'

      return {
        targetId: node.id,
        reviewId: node.performanceReviewProcess?.id,
        date: startsAtDate,
        bucket: `${label}-${index}`,
        color,
        score,
        scaleMin,
        scaleMax,
      }
    })
    .filter(Boolean)

  const lastScore = findLast(chartData, ({ score }) => score != null)?.score

  return {
    title: i18n.t('insights.widgets.performance.title'),
    chartData,
    lastScore,
  }
}

type PreparedData = ReturnType<typeof prepareData>

const PerformanceWidgetComponent: FC<{
  data: PreparedData
  employeeId: number
  onClickWidget?: () => void
}> = ({ employeeId, data: { title, chartData, lastScore }, onClickWidget }) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { employee: currentEmployee } = useCurrentSession()

  const linkUrl =
    currentEmployee?.id === employeeId
      ? navigation.profile.performance.fullPath()
      : navigation.employees.id.performance.fullPath({
          id: employeeId,
        })

  useBlocker(({ nextLocation }) => {
    if (linkUrl.endsWith(nextLocation.pathname)) {
      onClickWidget?.()
    }
    return false
  })

  return (
    <Widget
      header={{
        title,
        link: {
          title: i18n.t('insights.widgets.performance.title'),
          url: linkUrl,
        },
      }}
    >
      <Box gap="s12" className={'Performance_container'}>
        <Box flexDirection="row" alignItems="baseline" gap="s8">
          <span className="text-2xl font-semibold text-f1-foreground">
            {lastScore ? i18n.formatNumber(lastScore) : '–'}
          </span>
          <span className="text-lg text-f1-foreground-secondary">
            {i18n.t('insights.widgets.performance.current_score')}
          </span>
        </Box>
        <Box className={'Performance_chartContainer'}>
          <BarChart
            enabledGridY
            maxValue={max(chartData.map(({ scaleMax }) => scaleMax)) ?? 0}
            margin={{ top: 90, bottom: 50, left: 30, right: 20 }}
            colors={chartData.map(({ color }) => color).filter(Boolean)}
            colorBy="indexValue"
            pointer
            keys={['score']}
            indexBy="bucket"
            data={chartData.map(({ score, bucket }) => ({ score: score ?? 0, bucket }))}
            axisBottom={{
              format: (label: string) => label,
              renderTick: ({ x, y, value }) => {
                // Find the corresponding data point
                const dataItem = chartData.find((item) => item.bucket === value)
                return (
                  <g transform={`translate(${x},${y + 12})`}>
                    <text
                      textAnchor="middle"
                      dominantBaseline="middle"
                      style={{ fontFamily: 'Inter', fontSize: 11, fill: gammaColors.grey800 }}
                    >
                      {value.split('-')[0]}
                    </text>
                    <text
                      textAnchor="middle"
                      dominantBaseline="middle"
                      dy="14"
                      className="font-semibold"
                      style={{ fontFamily: 'Inter', fontSize: 11 }}
                    >
                      {dataItem && dataItem.score ? i18n.formatNumber(dataItem.score) : '-'}
                    </text>
                  </g>
                )
              },
            }}
            onClick={(datum) => {
              const reviewData = chartData[datum.index]
              if (!reviewData?.reviewId || !reviewData?.targetId) return

              navigate(
                navigation.performanceProcess.reviews.review.target.fullPath({
                  reviewId: reviewData.reviewId,
                  targetId: reviewData.targetId,
                })
              )
            }}
          />
        </Box>
      </Box>
    </Widget>
  )
}

export const buildPerformanceWidget = () =>
  buildWidget({
    id: 'performance',
    document: PerformanceInsightDocument,
    isEmpty: (data) => data.chartData.every(({ score }) => !score),
    prepareData,
    component: PerformanceWidgetComponent,
    policies: ['seeEmployeePerformanceTab'],
  })
